import React, { useState, useCallback, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Upload, Play, Save, Trash2, Plus, Music, Edit, ArrowLeft, Heart, X, FileText, Mic2, Palette, Bookmark } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import HopeReelPlayer from './HopeReelPlayer';
import AffirmationTemplates from './AffirmationTemplates';
import EnhancedVoiceRecorder from './EnhancedVoiceRecorder';
import BackgroundMusicLibrary from './BackgroundMusicLibrary';

interface MediaFile {
  file: File | null;
  url: string;
  type: 'image' | 'video';
  duration?: number;
  existingPath?: string;
}

interface HopeReel {
  id: string;
  title: string;
  affirmations: string[];
  media_files: any;
  audio_file?: string;
  voice_recording?: string;
  background_music?: string;
  background_music_title?: string;
  category?: string;
  tags?: string[];
  is_draft?: boolean;
  created_at: string;
}

interface HopeReelCreatorProps {
  editingReel?: HopeReel | null;
  onStopEditing?: () => void;
  user: any;
}

export default function HopeReelCreator({
  editingReel,
  onStopEditing,
  user
}: HopeReelCreatorProps) {
  const [title, setTitle] = useState('');
  const [affirmations, setAffirmations] = useState(['I am stronger than I know', 'This too shall pass', 'I am worthy of love and peace']);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [savedReels, setSavedReels] = useState<HopeReel[]>([]);
  const [showPlayer, setShowPlayer] = useState(false);
  const [currentReel, setCurrentReel] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [totalDuration, setTotalDuration] = useState(0);
  const [customPrompts, setCustomPrompts] = useState<string[]>([]);
  const [newCustomWord, setNewCustomWord] = useState('');
  const [voiceRecording, setVoiceRecording] = useState<{ blob: Blob; url: string } | null>(null);
  const [backgroundMusic, setBackgroundMusic] = useState<{ url: string; title: string } | null>(null);
  const [category, setCategory] = useState('healing');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [isDraft, setIsDraft] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const { toast } = useToast();

  const hopePrompts = [
    "Strength", "Courage", "Resilience", "Hope", "Love", "Peace",
    "Healing", "Growth", "Joy", "Gratitude", "Faith", "Compassion",
    "Wisdom", "Breathe", "Trust", "Forgive",
    "Believe", "Worthy", "Enough", "Safe", "Calm", "Present",
    "Beautiful", "Strong", "Loved", "Supported", "Capable", "Brave"
  ];

  const categories = [
    { value: 'healing', label: 'Healing & Recovery' },
    { value: 'confidence', label: 'Confidence & Self-Worth' },
    { value: 'anxiety', label: 'Anxiety & Stress Relief' },
    { value: 'manifestation', label: 'Manifestation & Goals' },
    { value: 'relationships', label: 'Relationships & Love' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'motivation', label: 'Motivation & Success' },
    { value: 'wellness', label: 'Health & Wellness' }
  ];

  const suggestedTags = [
    'morning-routine', 'evening-ritual', 'quick-boost', 'deep-healing',
    'confidence-building', 'stress-relief', 'self-love', 'motivation',
    'gratitude', 'forgiveness', 'abundance', 'peace'
  ];

  // Calculate total duration whenever media files change
  useEffect(() => {
    const total = mediaFiles.reduce((sum, file) => {
      return sum + (file.duration || 0);
    }, 0);
    setTotalDuration(total);
  }, [mediaFiles]);

  // Load editing reel data
  useEffect(() => {
    if (editingReel) {
      setIsEditing(true);
      setTitle(editingReel.title);
      setAffirmations([
        editingReel.affirmations[0] || 'I am stronger than I know', 
        editingReel.affirmations[1] || 'This too shall pass', 
        editingReel.affirmations[2] || 'I am worthy of love and peace'
      ]);
      loadEditingMediaFiles(editingReel);
    }
  }, [editingReel]);

  const loadEditingMediaFiles = async (reel: HopeReel) => {
    try {
      setMediaFiles([]);
      setAudioFile(null);
      setAudioUrl('');

      // Load media URLs from storage
      const mediaUrls = await Promise.all(
        reel.media_files.map(async (mediaFile: any) => {
          if (mediaFile.path) {
            const { data } = await supabase.storage
              .from('hope-reel-media')
              .createSignedUrl(mediaFile.path, 3600);
            return { ...mediaFile, url: data?.signedUrl };
          }
          return mediaFile;
        })
      );

      // Convert to MediaFile format for editing
      const editingMediaFiles: MediaFile[] = mediaUrls.map((mediaFile: any) => ({
        file: null,
        url: mediaFile.url || '',
        type: mediaFile.type,
        duration: mediaFile.duration,
        existingPath: mediaFile.path
      }));
      setMediaFiles(editingMediaFiles);

      // Load audio file if exists
      if (reel.audio_file) {
        const { data } = await supabase.storage
          .from('hope-reel-audio')
          .createSignedUrl(reel.audio_file, 3600);
        if (data?.signedUrl) {
          setAudioUrl(data.signedUrl);
        }
      }
    } catch (error) {
      console.error('Error loading editing media files:', error);
      toast({
        title: "Error",
        description: "Failed to load media files for editing",
        variant: "destructive",
      });
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setTitle('');
    setAffirmations(['I am stronger than I know', 'This too shall pass', 'I am worthy of love and peace']);
    setMediaFiles([]);
    setAudioFile(null);
    setAudioUrl('');
    if (onStopEditing) {
      onStopEditing();
    }
  };

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const videoFiles = files.filter(f => f.type.startsWith('video/'));

    // Compress images if any
    if (imageFiles.length > 0) {
      toast({
        title: 'Compressing images...',
        description: 'Optimising your images',
      });

      try {
        const { compressMultipleImages } = await import('@/utils/imageCompression');
        const compressed = await compressMultipleImages(imageFiles, 20);

        compressed.forEach(({ file, error }) => {
          if (!error) {
            const url = URL.createObjectURL(file);
            setMediaFiles(prev => [...prev, {
              file,
              url,
              type: 'image',
              duration: 4 // 4 seconds for images
            }]);
          } else {
            toast({
              title: 'Image failed',
              description: error,
              variant: 'destructive',
            });
          }
        });

        const totalSavings = compressed.reduce(
          (acc, c) => acc + (c.originalSize - c.compressedSize),
          0
        ) / 1024 / 1024;

        if (totalSavings > 0) {
          toast({
            title: 'Images ready!',
            description: `Saved ${totalSavings.toFixed(1)}MB`,
          });
        }
      } catch (error) {
        console.error('Error compressing images:', error);
        // Fallback to original images
        imageFiles.forEach(file => {
          const url = URL.createObjectURL(file);
          setMediaFiles(prev => [...prev, { file, url, type: 'image', duration: 4 }]);
        });
      }
    }

    // Handle video files (no compression)
    videoFiles.forEach(file => {
      const url = URL.createObjectURL(file);
      setMediaFiles(prev => [...prev, {
        file,
        url,
        type: 'video',
        duration: undefined
      }]);

      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        setMediaFiles(prev => prev.map(item => 
          item.url === url 
            ? { ...item, duration: Math.ceil(video.duration) }
            : item
        ));
      };
      video.src = url;
    });

    event.target.value = '';
  }, [toast]);

  const handleAudioSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('audio/')) {
      setAudioFile(file);
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
    }
    event.target.value = '';
  }, []);

  const removeMediaFile = (index: number) => {
    const fileToRemove = mediaFiles[index];
    if (fileToRemove.url && !fileToRemove.existingPath) {
      URL.revokeObjectURL(fileToRemove.url);
    }
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeAudioFile = () => {
    if (audioUrl && !audioFile) {
      // Don't revoke URL for existing files
    } else if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioFile(null);
    setAudioUrl('');
  };

  const addAffirmation = () => {
    setAffirmations(prev => [...prev, '']);
  };

  const updateAffirmation = (index: number, value: string) => {
    setAffirmations(prev => prev.map((aff, i) => i === index ? value : aff));
  };

  const removeAffirmation = (index: number) => {
    if (affirmations.length > 1) {
      setAffirmations(prev => prev.filter((_, i) => i !== index));
    }
  };

  const addCustomWord = () => {
    const word = newCustomWord.trim();
    if (word && !customPrompts.includes(word) && !hopePrompts.includes(word)) {
      setCustomPrompts(prev => [...prev, word]);
      setNewCustomWord('');
    }
  };

  const removeCustomWord = (wordToRemove: string) => {
    setCustomPrompts(prev => prev.filter(word => word !== wordToRemove));
  };

  const handleVoiceRecording = (blob: Blob, url: string) => {
    setVoiceRecording({ blob, url });
    toast({
      title: "Voice recording saved",
      description: "Your personal affirmation has been recorded",
    });
  };

  const handleBackgroundMusic = (musicUrl: string, title: string) => {
    setBackgroundMusic({ url: musicUrl, title });
    toast({
      title: "Background music selected",
      description: `"${title}" will enhance your Hope Reel`,
    });
  };

  const handleSelectAffirmation = (affirmation: string) => {
    const emptyIndex = affirmations.findIndex(a => a === '');
    if (emptyIndex !== -1) {
      const newAffirmations = [...affirmations];
      newAffirmations[emptyIndex] = affirmation;
      setAffirmations(newAffirmations);
    } else if (affirmations.length < 5) {
      setAffirmations([...affirmations, affirmation]);
    } else {
      setAffirmations([...affirmations.slice(1), affirmation]);
    }
  };

  const handleUseTemplate = (templateAffirmations: string[]) => {
    setAffirmations(templateAffirmations);
    toast({
      title: "Template applied",
      description: "Affirmations have been updated with the template",
    });
  };

  const addTag = () => {
    const tag = newTag.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const addSuggestedTag = (tag: string) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };

  const usePrompt = (prompt: string) => {
    const emptyIndex = affirmations.findIndex(aff => aff === '');
    if (emptyIndex !== -1) {
      updateAffirmation(emptyIndex, prompt);
    } else {
      setAffirmations(prev => [...prev, prompt]);
    }
  };

  const previewReel = () => {
    if (mediaFiles.length === 0) {
      toast({
        title: "No Media",
        description: "Please add at least one image or video to preview.",
        variant: "destructive",
      });
      return;
    }

    const previewReel = {
      id: 'preview',
      title: title || 'Hope Reel Preview',
      affirmations: affirmations.filter(aff => aff.trim() !== ''),
      media_files: mediaFiles.map(mf => ({
        type: mf.type,
        url: mf.url,
        duration: mf.duration
      })),
      audio_file: audioUrl
    };

    setCurrentReel(previewReel);
    setShowPlayer(true);
  };

  const saveReel = async () => {
    if (!user?.id) {
      toast({
        title: "Authentication required",
        description: "Please log in to save your Hope Reel.",
        variant: "destructive",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        title: "Title required",
        description: "Please enter a title for your Hope Reel.",
        variant: "destructive",
      });
      return;
    }

    if (mediaFiles.length === 0) {
      toast({
        title: "Media required",
        description: "Please add at least one image or video.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);

      // Upload media files
      const uploadedMediaFiles = await Promise.all(
        mediaFiles.map(async (mediaFile, index) => {
          if (mediaFile.existingPath) {
            // Keep existing file
            return {
              type: mediaFile.type,
              path: mediaFile.existingPath,
              duration: mediaFile.duration
            };
          }

          if (!mediaFile.file) return null;

          const fileExt = mediaFile.file.name.split('.').pop();
          const fileName = `${user.id}/${Date.now()}-${index}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('hope-reel-media')
            .upload(fileName, mediaFile.file);

          if (uploadError) throw uploadError;

          return {
            type: mediaFile.type,
            path: fileName,
            duration: mediaFile.duration
          };
        })
      );

      const validMediaFiles = uploadedMediaFiles.filter(file => file !== null);

      // Upload audio file
      let audioPath = null;
      if (audioFile) {
        const audioExt = audioFile.name.split('.').pop();
        const audioFileName = `${user.id}/${Date.now()}-audio.${audioExt}`;
        
        const { error: audioUploadError } = await supabase.storage
          .from('hope-reel-audio')
          .upload(audioFileName, audioFile);

        if (audioUploadError) throw audioUploadError;
        audioPath = audioFileName;
      } else if (editingReel?.audio_file && audioUrl) {
        // Keep existing audio file
        audioPath = editingReel.audio_file;
      }

      // Save to database
      const reelData = {
        user_id: user.id,
        title: title.trim(),
        affirmations: affirmations.filter(aff => aff.trim() !== ''),
        media_files: validMediaFiles,
        audio_file: audioPath
      };

      let result;
      if (isEditing && editingReel) {
        result = await supabase
          .from('hope_reels')
          .update(reelData)
          .eq('id', editingReel.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from('hope_reels')
          .insert(reelData)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      toast({
        title: "Success!",
        description: `Hope Reel ${isEditing ? 'updated' : 'created'} successfully!`,
      });

      // Reset form
      setTitle('');
      setAffirmations(['I am stronger than I know', 'This too shall pass', 'I am worthy of love and peace']);
      setMediaFiles([]);
      setAudioFile(null);
      setAudioUrl('');
      setIsEditing(false);
      
      if (onStopEditing) {
        onStopEditing();
      }

      // Reload saved reels
      loadReels();

    } catch (error) {
      console.error('Error saving Hope Reel:', error);
      toast({
        title: "Error",
        description: "Failed to save Hope Reel. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadReels = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('hope_reels')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedReels(data || []);
    } catch (error) {
      console.error('Error loading Hope Reels:', error);
    }
  };

  const playReel = (reel: HopeReel) => {
    setCurrentReel(reel);
    setShowPlayer(true);
  };

  const deleteReel = async (id: string) => {
    try {
      const { error } = await supabase
        .from('hope_reels')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Deleted",
        description: "Hope Reel deleted successfully.",
      });

      loadReels();
    } catch (error) {
      console.error('Error deleting Hope Reel:', error);
      toast({
        title: "Error",
        description: "Failed to delete Hope Reel.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    loadReels();
  }, [user?.id]);

  if (showPlayer && currentReel) {
    return <HopeReelPlayer reel={currentReel} onClose={() => setShowPlayer(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            {isEditing && (
              <Button variant="outline" size="sm" onClick={cancelEditing}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to List
              </Button>
            )}
            <Heart className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Hope Reel Creator</h1>
          </div>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Create an uplifting video reel with your memories, affirmations, and hope-filled moments. 
            Let these reels remind you of your strength during difficult times.
          </p>
        </div>

        {/* Creator Form */}
        <Card className="border-primary/20 bg-gradient-therapeutic">
          <div className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-2">Hope Reel Title</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="My Strength and Hope"
                className="text-lg"
              />
            </div>

            {/* Quick Prompts */}
            <div>
              <label className="block text-sm font-medium mb-2">Quick Affirmation Prompts</label>
              
              {/* Add Custom Word Input */}
              <div className="flex gap-2 mb-4">
                <Input
                  value={newCustomWord}
                  onChange={(e) => setNewCustomWord(e.target.value)}
                  placeholder="Add your own word..."
                  className="flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && addCustomWord()}
                />
                <Button 
                  onClick={addCustomWord} 
                  variant="outline" 
                  size="sm"
                  disabled={!newCustomWord.trim()}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Default Prompts */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {hopePrompts.map((prompt, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => usePrompt(prompt)}
                    className="text-xs h-auto py-2 px-3 whitespace-normal text-left"
                  >
                    {prompt}
                  </Button>
                ))}
              </div>

              {/* Custom Prompts */}
              {customPrompts.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Your custom words:</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {customPrompts.map((prompt, index) => (
                      <div key={`custom-${index}`} className="relative group">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => usePrompt(prompt)}
                          className="text-xs h-auto py-2 px-3 pr-8 whitespace-normal text-left w-full bg-primary/5 border-primary/30"
                        >
                          {prompt}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCustomWord(prompt)}
                          className="absolute top-0 right-0 h-full w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Affirmations */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium">Hope Affirmations</label>
                <Button variant="ghost" size="sm" onClick={addAffirmation}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-3">
                {affirmations.map((affirmation, index) => (
                  <div key={index} className="flex gap-2">
                    <Textarea
                      value={affirmation}
                      onChange={(e) => updateAffirmation(index, e.target.value)}
                      placeholder="Enter a positive affirmation..."
                      rows={2}
                    />
                    {affirmations.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAffirmation(index)}
                        className="text-destructive hover:text-destructive/80"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Media Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">Images & Videos</label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="media-upload"
                />
                <label htmlFor="media-upload" className="cursor-pointer">
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">Click to upload images and videos</p>
                  <p className="text-xs text-muted-foreground mt-1">Support for JPG, PNG, MP4, MOV</p>
                </label>
              </div>

              {/* Media Preview */}
              {mediaFiles.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {mediaFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      {file.type === 'image' ? (
                        <img
                          src={file.url}
                          alt={`Media ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      ) : (
                        <video
                          src={file.url}
                          className="w-full h-32 object-cover rounded-lg"
                          controls={false}
                        />
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeMediaFile(index)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                      <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                        {file.duration}s
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Audio Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">Background Music (Optional)</label>
              <div className="border-2 border-dashed border-border rounded-lg p-4">
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioSelect}
                  className="hidden"
                  id="audio-upload"
                />
                <label htmlFor="audio-upload" className="cursor-pointer flex items-center gap-2">
                  <Music className="h-6 w-6 text-muted-foreground" />
                  <span className="text-muted-foreground">Add background music</span>
                </label>
                {audioUrl && (
                  <div className="mt-3 flex items-center justify-between">
                    <audio controls className="flex-1 mr-2">
                      <source src={audioUrl} />
                    </audio>
                    <Button variant="ghost" size="sm" onClick={removeAudioFile}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={previewReel}
                variant="outline"
                disabled={mediaFiles.length === 0}
              >
                <Play className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={saveReel}
                disabled={isLoading || !title.trim() || mediaFiles.length === 0}
                className="bg-primary hover:bg-primary/90"
              >
                <Save className="w-4 h-4 mr-2" />
                {isLoading ? 'Saving...' : isEditing ? 'Update Reel' : 'Save Reel'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Saved Reels */}
        {savedReels.length > 0 && (
          <Card>
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Your Hope Reels</h2>
              <div className="grid gap-4">
                {savedReels.map((reel) => (
                  <div key={reel.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h3 className="font-medium">{reel.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {reel.affirmations.length} affirmations • {reel.media_files.length} media files
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Created {new Date(reel.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => playReel(reel)}>
                        <Play className="w-4 h-4 mr-1" />
                        Play
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteReel(reel.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
